/**
 * 경로 지도 — 소개영상의 "경로" 장면과 일차 사이 미니맵 전환에 쓴다.
 *
 * 재료는 대본과 함께 온 route([{ name, lat, lng, day }], erp-video 가 좌표를 정리해서 준다).
 * 배경은 Google Static Maps(다크 스타일 1장)를 받아 쓰고, 못 받으면(키 없음·API 미활성·네트워크) 점·선·지명만 그리는
 * 추상 약도로 대신한다. 두 경우 모두 같은 웹 메르카토르 투영으로 화면 좌표를 만든다.
 *
 * Static Maps 는 정수 줌만 받으므로 배경이 있을 땐 정수 줌으로 맞추고(여백이 조금 남는다), 약도는 소수 줌으로 꽉 채운다.
 */

const TILE = 256;
/** 도시 하나만 있을 때의 줌 */
const SINGLE_ZOOM = 10;
const MAX_ZOOM = 13;
const MIN_ZOOM = 2;

/** 위경도 → 웹 메르카토르 픽셀 (줌 zoom 기준, 세계 전체가 256·2^zoom 픽셀) */
export function project(lat, lng, zoom) {
  const n = TILE * 2 ** zoom;
  const x = ((lng + 180) / 360) * n;
  const s = Math.sin((Math.max(-85, Math.min(85, lat)) * Math.PI) / 180);
  const y = (0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI)) * n;
  return { x, y };
}

/** 점들이 w×h(여백 pad) 안에 들어가는 가장 큰 줌. integerOnly 면 정수로 내림. */
function fitZoom(points, w, h, pad, integerOnly) {
  if (points.length < 2) return SINGLE_ZOOM;
  const p0 = points.map((p) => project(p.lat, p.lng, 0));
  const dx = Math.max(...p0.map((p) => p.x)) - Math.min(...p0.map((p) => p.x));
  const dy = Math.max(...p0.map((p) => p.y)) - Math.min(...p0.map((p) => p.y));
  const zx = Math.log2(Math.max(1, w - 2 * pad) / Math.max(dx, 1e-6));
  const zy = Math.log2(Math.max(1, h - 2 * pad) / Math.max(dy, 1e-6));
  let z = Math.min(zx, zy, MAX_ZOOM);
  if (integerOnly) z = Math.floor(z);
  return Math.max(MIN_ZOOM, z);
}

/** 점들의 가운데(위경도). 투영 공간에서 가운데를 잡아 위도 왜곡을 피한다. */
function centerOf(points) {
  const p0 = points.map((p) => project(p.lat, p.lng, 0));
  const cx = (Math.max(...p0.map((p) => p.x)) + Math.min(...p0.map((p) => p.x))) / 2;
  const cy = (Math.max(...p0.map((p) => p.y)) + Math.min(...p0.map((p) => p.y))) / 2;
  return { x: cx, y: cy }; // 줌 0 기준 픽셀
}

/** 다크 스타일. 사진 사이에 끼어도 튀지 않게 채도를 죽이고, 도로·POI 라벨은 지운다. */
const STATIC_STYLES = [
  'element:geometry|color:0x243149',
  'element:labels.text.fill|color:0x9fb3c8',
  'element:labels.text.stroke|color:0x243149',
  'element:labels.icon|visibility:off',
  'feature:administrative|element:geometry|color:0x3a4a63',
  'feature:administrative.country|element:geometry.stroke|color:0x5b6f8f',
  'feature:administrative.locality|element:labels.text.fill|color:0xd6e0ec',
  'feature:administrative.land_parcel|visibility:off',
  'feature:administrative.neighborhood|visibility:off',
  'feature:poi|visibility:off',
  'feature:road|visibility:off',
  'feature:road.highway|element:geometry|color:0x2b3a52|visibility:simplified',
  'feature:road|element:labels|visibility:off',
  'feature:transit|visibility:off',
  'feature:landscape.natural|element:geometry|color:0x1f2b40',
  'feature:water|element:geometry|color:0x0f1626',
  'feature:water|element:labels.text.fill|color:0x4f637f',
];

function staticMapUrl({ key, centerLat, centerLng, zoom, w, h }) {
  const params = [
    `center=${centerLat.toFixed(5)},${centerLng.toFixed(5)}`,
    `zoom=${zoom}`,
    `size=${w}x${h}`,
    'scale=2',
    'maptype=roadmap',
    'language=ko',
    ...STATIC_STYLES.map((s) => `style=${encodeURIComponent(s)}`),
    `key=${encodeURIComponent(key)}`,
  ];
  return `https://maps.googleapis.com/maps/api/staticmap?${params.join('&')}`;
}

/** 줌 0 픽셀 → 위경도 (Static Maps 의 center 파라미터용) */
function unproject(x, y, zoom = 0) {
  const n = TILE * 2 ** zoom;
  const lng = (x / n) * 360 - 180;
  const yy = 0.5 - y / n; // = atanh(sin φ) / 2π
  const lat = (Math.atan(Math.sinh(yy * 2 * Math.PI)) * 180) / Math.PI;
  return { lat, lng };
}

/**
 * 경로 지도 재료를 만든다. loadImage 는 introVideoBuilder 의 것(CORS 우회 포함)을 넘겨받는다 — 순환 import 를 피하려고.
 * @returns {Promise<null|{ image: ImageBitmap|null, points: Array<{name, day, lat, lng, x, y}>, width, height }>}
 *   x, y 는 화면(width×height) 좌표. image 가 null 이면 추상 약도로 그린다.
 */
export async function buildRouteMap({ route, width, height, googleMapsKey, imageCache, loadImage }) {
  const pts = (Array.isArray(route) ? route : []).filter((p) => p && Number.isFinite(p.lat) && Number.isFinite(p.lng));
  if (pts.length < 2) return null;
  const u = Math.min(width, height);
  const c0 = centerOf(pts);
  const center = unproject(c0.x, c0.y, 0);

  // 1) Static Maps: 프레임의 절반 크기(scale=2 로 받으면 프레임과 같은 픽셀)를 요청한다. 최대 640.
  let image = null;
  let zoom = null;
  const mapW1 = Math.min(640, Math.round(width / 2));
  const mapH1 = Math.min(640, Math.round(height / 2));
  if (googleMapsKey && loadImage) {
    // 여백은 짧은 변 기준 10% — 가로 지도(640×360)에서 긴 변 기준으로 잡으면 줌이 한 단계 멀어져 대륙 지도가 된다
    const z = fitZoom(pts, mapW1, mapH1, Math.round(Math.min(mapW1, mapH1) * 0.1), true);
    try {
      image = await loadImage(staticMapUrl({ key: googleMapsKey, centerLat: center.lat, centerLng: center.lng, zoom: z, w: mapW1, h: mapH1 }), imageCache);
    } catch {
      image = null;
    }
    // 오류 응답도 이미지로 오는 경우가 있어(회색 바탕에 글자) 최소 크기로 걸러낸다
    if (image && (image.width < mapW1 || image.height < mapH1)) image = null;
    if (image) zoom = z;
  }
  // 2) 약도: 소수 줌으로 여백을 두고 꽉 채운다
  if (zoom == null) zoom = fitZoom(pts, width, height, Math.round(u * 0.16), false);

  // 화면 좌표: 배경이 있으면 프레임 픽셀 = 지도 픽셀(scale 1) × k
  const k = image ? width / mapW1 : 1;
  const c = project(center.lat, center.lng, zoom);
  const points = pts.map((p) => {
    const q = project(p.lat, p.lng, zoom);
    return { ...p, x: width / 2 + (q.x - c.x) * k, y: height / 2 + (q.y - c.y) * k };
  });
  return { image, points, width, height };
}

// ── 그리기 ───────────────────────────────────────────────────────

const clamp01 = (v) => Math.max(0, Math.min(1, v));
const easeInOut = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);
const easeOut = (t) => 1 - Math.pow(1 - t, 3);
const FONT_STACK = "'Pretendard', 'Malgun Gothic', 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif";
const ROUTE_COLOR = '#fbbf24';

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/** 배경이 없을 때의 약도 바탕: 짙은 남색 그라데이션 + 옅은 격자 + 은은한 빛 */
function drawAbstractBackground(ctx, width, height, u) {
  const g = ctx.createLinearGradient(0, 0, width, height);
  g.addColorStop(0, '#16203a');
  g.addColorStop(1, '#0b1020');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, width, height);
  ctx.save();
  ctx.strokeStyle = 'rgba(255,255,255,0.045)';
  ctx.lineWidth = 1;
  const step = Math.round(u * 0.09);
  for (let x = step; x < width; x += step) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, height); ctx.stroke(); }
  for (let y = step; y < height; y += step) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(width, y); ctx.stroke(); }
  const glow = ctx.createRadialGradient(width * 0.5, height * 0.45, 0, width * 0.5, height * 0.45, Math.max(width, height) * 0.6);
  glow.addColorStop(0, 'rgba(80,120,200,0.18)');
  glow.addColorStop(1, 'rgba(80,120,200,0)');
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, width, height);
  ctx.restore();
}

/** 두 점 사이를 p(0..1)만큼 그린다 */
function strokeSegment(ctx, a, b, p, u, alpha) {
  const x = a.x + (b.x - a.x) * p;
  const y = a.y + (b.y - a.y) * p;
  ctx.save();
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  // 글로우
  ctx.globalAlpha = alpha * 0.35;
  ctx.strokeStyle = ROUTE_COLOR;
  ctx.lineWidth = u * 0.014;
  ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(x, y); ctx.stroke();
  // 본선
  ctx.globalAlpha = alpha;
  ctx.lineWidth = u * 0.005;
  ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(x, y); ctx.stroke();
  ctx.restore();
}

function drawPoint(ctx, p, u, alpha, { pulse = 0, dim = false } = {}) {
  ctx.save();
  ctx.globalAlpha = alpha;
  if (pulse > 0) {
    // 도착 강조: 커지며 사라지는 링
    ctx.strokeStyle = ROUTE_COLOR;
    ctx.lineWidth = u * 0.004;
    ctx.globalAlpha = alpha * (1 - pulse);
    ctx.beginPath(); ctx.arc(p.x, p.y, u * (0.012 + 0.04 * pulse), 0, Math.PI * 2); ctx.stroke();
    ctx.globalAlpha = alpha;
  }
  ctx.fillStyle = dim ? 'rgba(255,255,255,0.75)' : '#ffffff';
  ctx.beginPath(); ctx.arc(p.x, p.y, u * 0.012, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = ROUTE_COLOR;
  ctx.beginPath(); ctx.arc(p.x, p.y, u * 0.0065, 0, Math.PI * 2); ctx.fill();
  ctx.restore();
}

/** 지명 알약. 점 오른쪽(화면 오른쪽 끝이면 왼쪽), 홀짝으로 위/아래를 바꿔 겹침을 줄인다. */
function drawLabel(ctx, p, idx, width, u, alpha) {
  if (!p.name) return;
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.font = `600 ${Math.round(u * 0.034)}px ${FONT_STACK}`;
  const padX = u * 0.016;
  const h = Math.round(u * 0.052);
  const w = ctx.measureText(p.name).width + padX * 2;
  const right = p.x < width * 0.72;
  const x = right ? p.x + u * 0.02 : p.x - u * 0.02 - w;
  const y = (idx % 2 === 0 ? p.y - h - u * 0.012 : p.y + u * 0.012);
  ctx.fillStyle = 'rgba(8,12,24,0.78)';
  roundRect(ctx, x, y, w, h, h / 2);
  ctx.fill();
  ctx.strokeStyle = 'rgba(255,255,255,0.25)';
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.fillStyle = '#fff';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText(p.name, x + padX, y + h / 2 + u * 0.001);
  ctx.restore();
}

/**
 * 지도 샷을 그린다.
 * @param shot { layout:'map', mode:'full'|'leg', start, end, fromIdx?, toIdx?, map }
 *   full — 경로 장면: 점이 순서대로 켜지고 선이 이어진다
 *   leg  — 일차 전환: 지나온 구간은 희미하게, fromIdx→toIdx 구간이 그려지며 도착점이 반짝인다
 */
export function drawRouteMap(ctx, shot, t, width, height, alpha) {
  const map = shot.map;
  const u = Math.min(width, height);
  const dur = Math.max(0.001, shot.end - shot.start);
  const p = clamp01((t - shot.start) / dur);
  const pts = map.points;
  const n = pts.length;

  // 카메라: 경로 장면은 천천히 다가가고, 전환은 구간 가운데를 확대해 본다
  let scale = 1;
  let px = width / 2;
  let py = height / 2;
  if (shot.mode === 'leg' && n) {
    const a = pts[Math.max(0, shot.fromIdx)];
    const b = pts[Math.min(n - 1, shot.toIdx)];
    scale = 1.3;
    px = (a.x + b.x) / 2;
    py = (a.y + b.y) / 2;
  } else {
    scale = 1 + 0.07 * easeInOut(p);
  }
  // 확대해도 프레임 밖(지도 없는 곳)이 보이지 않게 이동량을 제한한다
  let tx = px - scale * px;
  let ty = py - scale * py;
  tx = Math.max(width - scale * width, Math.min(0, tx));
  ty = Math.max(height - scale * height, Math.min(0, ty));

  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.translate(tx, ty);
  ctx.scale(scale, scale);
  if (map.image) ctx.drawImage(map.image, 0, 0, width, height);
  else drawAbstractBackground(ctx, width, height, u);
  // 배경 위를 살짝 어둡게 — 선·글자가 잘 보이게
  ctx.fillStyle = 'rgba(0,0,0,0.12)';
  ctx.fillRect(0, 0, width, height);
  // 아래 그리기 함수들은 globalAlpha 를 절대값으로 놓으므로, 전환 중 투명도(alpha)를 곱해서 넘긴다

  if (shot.mode === 'leg') {
    const from = Math.max(0, shot.fromIdx);
    const to = Math.min(n - 1, Math.max(from, shot.toIdx));
    // 지나온 구간
    for (let i = 1; i <= from; i++) strokeSegment(ctx, pts[i - 1], pts[i], 1, u, alpha * (0.45));
    for (let i = 0; i <= from; i++) drawPoint(ctx, pts[i], u, alpha * (0.8), { dim: true });
    // 이번 구간: 0.1~0.8 사이에 그려지고, 여러 구간이면 나눠 그린다
    const segs = Math.max(1, to - from);
    const prog = easeInOut(clamp01((p - 0.1) / 0.7)) * segs;
    for (let s = 0; s < segs; s++) {
      const sp = clamp01(prog - s);
      if (sp <= 0) break;
      strokeSegment(ctx, pts[from + s], pts[from + s + 1], sp, u, alpha * (1));
      if (sp >= 1 || s === segs - 1) {
        const arrived = sp >= 1;
        const pulse = arrived ? clamp01((p - 0.8) / 0.2) : 0;
        if (arrived) drawPoint(ctx, pts[from + s + 1], u, alpha * (1), { pulse });
      }
    }
    drawLabel(ctx, pts[from], from, width, u, alpha * (0.9));
    const labelA = clamp01((prog - (segs - 1) - 0.6) / 0.3);
    if (labelA > 0) drawLabel(ctx, pts[to], to, width, u, alpha * (labelA));
  } else {
    // 경로 장면: 0.12 부터 0.9 까지 n-1 구간을 차례로. 끝을 0.6 구간만큼 넘기는 이유: 마지막 점의 라벨·펄스가
    // "도착 후 지난 시간(since)" 으로 뜨는데, 딱 n-1 에서 멈추면 since 가 0 이라 마지막 지명이 영영 안 뜬다.
    const reveal = easeOut(clamp01((p - 0.12) / 0.78)) * (n - 1 + 0.6);
    drawPoint(ctx, pts[0], u, alpha * (1));
    drawLabel(ctx, pts[0], 0, width, u, alpha * (clamp01(p / 0.15)));
    for (let i = 1; i < n; i++) {
      const sp = clamp01(reveal - (i - 1));
      if (sp <= 0) break;
      strokeSegment(ctx, pts[i - 1], pts[i], sp, u, alpha * (1));
      if (sp >= 1 || i === n - 1) {
        const arrived = sp >= 1;
        if (arrived) {
          const since = reveal - i; // 도착 후 지난 구간 수
          drawPoint(ctx, pts[i], u, alpha * (1), { pulse: clamp01(since / 0.6) });
          drawLabel(ctx, pts[i], i, width, u, alpha * (clamp01(since / 0.25)));
        }
      }
    }
  }
  ctx.restore();
}
